import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';
import Async from 'components/Async';
import useIntegrityAdvocate from 'components/IntegrityAdvocate/hooks/useIntegrityAdvocate';
import { useGATracking } from 'hooks/useGATracking';
import useNotification from 'hooks/useNotification';
import { DateTime } from 'luxon';
import store from 'store';

import { sendLessonAnalitics } from '@common/api/students';
import { checkRole } from '@common/components/RBAC';
import { LESSON_HOMEWORK_START_TIME } from '@common/constants/GaEvents';
import { appId } from '@common/constants/integrityadvocateserver';
import useId from '@common/helpers/useId';
import useModel from '@common/helpers/useModel';
import ChunksModel, { IChunksModel } from '@common/models/Chunks/Chunks';
import { IStudentLessonModel } from '@common/models/StudentLessons/Studentlesson';

import Chunks from './Chunks';
import LessonSectionDescription from './Description';
import { StyledLessonGridItem, StyledLessonSectionWrap } from './style';

interface PropsType {
	lesson: IStudentLessonModel;
	workType: 'homework' | 'classwork';
}

function LessonSection({ lesson, workType }: PropsType) {
	const { t } = useTranslation();
	const { sendEvent } = useGATracking();
	const params = useParams();

	const { programGroupId } = useId(['programGroupId']);
	const { endSession } = useIntegrityAdvocate(
		appId,
		lesson?.schedule_program?.id,
		store?.profile?.student?.id,
		'1',
	);

	const [loading, setLoading] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const notificate = useNotification();
	const lessonWork = lesson.lesson_info[workType];

	if (!lessonWork) {
		return null;
	}

	const chunksModel = useModel(
		ChunksModel,
		{
			lesson_work_id: lessonWork.id,
			schedule_lesson_id: lesson.schedule_lesson_id,
			student_id: store.currentStudentId,
			isIndiv: !programGroupId,
		},
		[lessonWork, lesson.schedule_lesson_id, store.currentStudentId],
	) as IChunksModel;

	const showLessonInfoBoxCondition = lessonWork?.image || lessonWork?.description;

	useEffect(() => {
		if (workType === 'homework') {
			const lessonStartTime = DateTime.local().toFormat('yyyy-MM-dd HH:mm:ss');
			const { programId, groupId, programGroupId, scheduleLessonId } = params;
			sendEvent(LESSON_HOMEWORK_START_TIME, {
				start_time: lessonStartTime,
				user_id: store.profile.user_id,
				student_id: store?.currentStudentId,
				program_id: programId,
				group_id: groupId,
				program_group_id: programGroupId,
				schedule_lesson_id: scheduleLessonId,
			});
			try {
				if (!checkRole('Parent')) {
					sendLessonAnalitics({
						name: 'completion',
						schedule_lesson_id: Number(scheduleLessonId),
						start_time: lessonStartTime,
					});
				}
			} catch (e) {
				console.log(e);
			}
		}
	}, []);

	return (
		<Async loading={chunksModel.loading}>
			<StyledLessonSectionWrap>
				<StyledLessonGridItem sx={{ overflow: 'hidden' }}>
					{showLessonInfoBoxCondition && (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '12px',
								padding: '12px',
							}}
						>
							{lessonWork?.image && (
								<Box
									component="img"
									src={lessonWork.image}
									alt="Lesson Image"
									sx={{ borderRadius: '24px' }}
								/>
							)}
							{lessonWork?.description && (
								<Box
									sx={{
										marginLeft: '4px',
										'& p': { margin: '0 0 16px 0' },
										'& img': { maxWidth: '100%' },
									}}
									className="ck-content"
									dangerouslySetInnerHTML={{
										__html: lessonWork.description,
									}}
								/>
							)}
						</Box>
					)}
					<Chunks
						chunksModel={chunksModel}
						lessonModel={lesson}
						canBeForm={workType !== 'classwork'}
					/>
				</StyledLessonGridItem>

				<StyledLessonGridItem>
					<LessonSectionDescription chunksModel={chunksModel} lessonModel={lesson} />
					{!!lesson.canBeCompleted && (
						<LoadingButton
							variant="contained"
							loading={loading}
							sx={{
								fontSize: '13px',
								padding: '10px 16px',
								height: 'auto',
							}}
							onClick={async () => {
								try {
									setLoading(true);
									endSession();
									await lesson.complete();
									setIsSubmitted(true);
									notificate('success', t('lesson.complete'));
								} catch (e) {
									notificate('warning', t('lesson.errorComplete'), { withoutTitle: true });
									throw e;
								} finally {
									setLoading(false);
								}
							}}
							disabled={isSubmitted || !!lesson.gradebook.is_done || store.profile.viewOnlyMode}
						>
							COMPLETE
						</LoadingButton>
					)}
				</StyledLessonGridItem>
			</StyledLessonSectionWrap>
		</Async>
	);
}

export default observer(LessonSection);
